import type { Permission } from '@hearth/shared';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { useAuth } from '../../hooks/useAuth';
import { CatalogSection } from './sections/CatalogSection';
import { CustomFieldsSection } from './sections/CustomFieldsSection';
import { EmailSection } from './sections/EmailSection';
import { GroupsSection } from './sections/GroupsSection';
import { PipelineSection } from './sections/PipelineSection';
import { ProfileSection } from './sections/ProfileSection';
import { ScoringSection } from './sections/ScoringSection';
import { TerritoriesSection } from './sections/TerritoriesSection';
import { UsersSection } from './sections/UsersSection';

type Section = { path: string; label: string; group: string; permission?: Permission; element: ReactNode };

/** Every settings screen, in menu order. Sections a user may not manage are not shown at all. */
const SECTIONS: Section[] = [
  { path: 'profile', label: 'My profile', group: 'You', element: <ProfileSection /> },
  { path: 'organization', label: 'Organization', group: 'Company', permission: 'settings:manage', element: <GroupsSection keys={['general']} /> },
  { path: 'users', label: 'Users', group: 'Company', permission: 'users:manage', element: <UsersSection /> },
  { path: 'territories', label: 'Territories', group: 'Company', permission: 'settings:manage', element: <TerritoriesSection /> },
  { path: 'access', label: 'Access & security', group: 'Company', permission: 'settings:manage', element: <GroupsSection keys={['access', 'security']} /> },
  { path: 'pipeline', label: 'Stages & relationships', group: 'Sales process', permission: 'settings:manage', element: <PipelineSection /> },
  { path: 'scoring', label: 'Scoring & tiers', group: 'Sales process', permission: 'scoring:manage', element: <ScoringSection /> },
  { path: 'rules', label: 'Follow-up & data rules', group: 'Sales process', permission: 'scoring:manage', element: <GroupsSection keys={['nextAction', 'opportunities', 'dataQuality', 'duplicates']} /> },
  { path: 'catalog', label: 'Products, sources & tags', group: 'Sales process', permission: 'settings:manage', element: <CatalogSection /> },
  { path: 'custom-fields', label: 'Custom fields', group: 'Sales process', permission: 'settings:manage', element: <CustomFieldsSection /> },
  { path: 'email', label: 'Email provider & limits', group: 'Outreach', permission: 'settings:manage', element: <EmailSection /> },
  { path: 'compliance', label: 'Email compliance', group: 'Outreach', permission: 'settings:manage', element: <GroupsSection keys={['compliance']} /> },
  { path: 'automation', label: 'Automation', group: 'Outreach', permission: 'automation:manage', element: <GroupsSection keys={['automation']} /> },
];

export function SettingsPage() {
  const { can } = useAuth();
  const visible = SECTIONS.filter((section) => !section.permission || can(section.permission));
  const groups = [...new Set(visible.map((s) => s.group))];
  return (
    <Page width="wide">
      <PageHeader title="Settings" />
      <div className="grid gap-6 md:grid-cols-[13rem_minmax(0,1fr)]">
        <nav aria-label="Settings sections" className="space-y-4">
          {groups.map((group) => (
            <div key={group}>
              <p className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-slate-400">{group}</p>
              <ul>
                {visible
                  .filter((s) => s.group === group)
                  .map((section) => (
                    <li key={section.path}>
                      <NavLink
                        to={`/settings/${section.path}`}
                        className={({ isActive }) =>
                          clsx('block rounded-md px-2 py-1.5 text-[13px]', isActive ? 'bg-surface font-medium text-slate-900 shadow-sm' : 'text-slate-600 hover:bg-surface/60')
                        }
                      >
                        {section.label}
                      </NavLink>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="min-w-0 space-y-4">
          <Routes>
            <Route index element={<Navigate to={visible[1]?.path ?? 'profile'} replace />} />
            {visible.map((section) => (
              <Route key={section.path} path={section.path} element={section.element} />
            ))}
            <Route path="*" element={<Navigate to="profile" replace />} />
          </Routes>
        </div>
      </div>
    </Page>
  );
}
